import React, { useContext, useEffect, useState } from "react";
import { dashboardContext, Access } from "../Dashboard";
import { useQuery } from "@apollo/client";
import {
  Table,
  Pagination,
  RowsPerPageSelect,
} from "../../../components/Table";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { amountFormat } from "../../../utils/amountFormat";
import Modal from "../../../components/Modal/Modal";
import { IoSearchOutline } from "react-icons/io5";
import { useTransactionFilters } from "../../../hooks/useTransactionFilters";
import { GET_PLANS } from "../../../Qurries";
import Select from "react-select";
import CreatePlanForm from "./CreatePlan";
import { HiMiniXMark } from "react-icons/hi2";
import { CustomDropdownIndicator } from "../Settlement/Settlement";
import TransactionDateFilter from "../Transaction/components/TransactionDateFilter";

enum PlanType {
  ON_DEMAND = "ON_DEMAND",
  PERIODIC = "PERIODIC",
}

export interface PlanDetails {
  plan_id: string;
  plan_name: string;
  plan_type: PlanType;
  currency: string;
  plan_max_amount: number;
  plan_recurring_amount: number;
  plan_max_cycles: number;
  plan_intervals: number;
  plan_interval_type: string;
  plan_note: string;
  school_id: string;
}

function Plans() {
  const { user, isRedirectedUser, tokenData } = useContext(dashboardContext);
  const [plansData, setPlansData] = useState<any>([]);
  const [refetchLoading,] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [urlFilters,] = useTransactionFilters();
  const [currentPage, setCurrentPage] = useState<number>(
    Number(urlFilters.page) || 1
  );
  const [itemsPerRow, setItemsPerRow] = useState<any>({
    name: Number(urlFilters.limit) || 10,
  });
  // const [dateRange, setDateRange] = useState("");
  const DEFAULT_RANGE = {
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  };

  const [selectedRange, setSelectedRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>(DEFAULT_RANGE);

  const [dateRangeLabel, setDateRangeLabel] = useState<string>("");
  const [isDateRangeIsSelected, setIsDateRangeIsSelected] = useState(false);
  const [schoolId,] = useState<any>(urlFilters.school_id || null);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  const [searchFilter, setSearchFilter] = useState<string>("");
  const handleItemsPerPageChange = (value: { name: number }) => {
    setItemsPerRow(value);
    setCurrentPage(1);
  };

  const filterVariables = {
    page: currentPage,
    limit: itemsPerRow.name,

    ...(debouncedSearch.trim().length >= 2 && 
        searchFilter && 
        searchFilter !== "" && 
        searchFilter !== "Filter by"
      ? {
          search: debouncedSearch.trim(),
          searchBy: searchFilter,
        }
      : {}),

    school_id: schoolId || undefined,

    ...(isDateRangeIsSelected
      ? {
          startDate: selectedRange.startDate.toISOString(),
          endDate: selectedRange.endDate.toISOString(),
        }
      : {}),
  };

  const { data, loading, refetch } = useQuery(GET_PLANS, {
    variables: {
      filter: filterVariables,
    },
    fetchPolicy: "network-only",
  });

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const searchByOptions = [
    {
      label: "Plan ID",
      value: "plan_id",
    },
    {
      label: "Plan Name",
      value: "plan_name",
    },
    {
      label: "Plan Type",
      value: "plan_type",
    },
  ];

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    if (data?.getPlansMerchant?.data) {
      setPlansData(data?.getPlansMerchant);
    }
  }, [data?.getPlansMerchant?.data,data?.getPlansMerchant]);
  useEffect(() => {}, [plansData]);

  return (
    <div className="w-full pt-2">
      <Table
        pagination={false}
        heading={"Plans"}
        loading={loading || refetchLoading}
        copyContent={[3, 4]}
        exportBtn={true}
        searchBox={
          <div className="flex flex-col w-full">
            <div className="flex xl:!flex-row flex-col justify-between gap-2 w-full xl:items-center items-start mb-2">
              <div className="bg-[#EEF1F6] py-3 items-center flex px-3 xl:max-w-md max-w-[34rem] w-full rounded-[4px]">
                <input
                  className="text-xs pr-2 bg-transparent focus:outline-none w-full placeholder:font-normal"
                  type="text"
                  value={searchText}
                  placeholder="Search (Subs. ID, Ref. No., Plan Name, Mobile...)"
                  onChange={(e) => {
                    handleSearch(e.target.value);
                  }}
                />
                {searchFilter !== "" && searchText.length > 1 && (
                  <HiMiniXMark
                    onClick={() => {
                      setSearchText("");
                      setSearchFilter("");
                      setCurrentPage(1);
                    }}
                    className="text-[#1E1B59] cursor-pointer text-md mr-2 shrink-0"
                  />
                )}
                <Select
                  className="border-l-2 border-gray-400"
                  options={searchByOptions}
                  isSearchable={false}
                  components={{
                    DropdownIndicator: CustomDropdownIndicator,
                    IndicatorSeparator: () => null,
                  }}
                  onChange={(e: any) => {
                    setSearchFilter(e?.value || "");
                    setCurrentPage(1);
                  }}
                  placeholder={
                    <div className="text-[#1E1B59] -mt-1 capitalize text-[10px]">
                      {searchFilter === ""
                        ? "Filter by"
                        : searchByOptions.find((o) => o.value === searchFilter)
                            ?.label || "Filter by"}
                    </div>
                  }
                  value={
                    searchByOptions.find((opt) => opt.value === searchFilter) ||
                    null
                  }
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "transparent",
                      height: "20px",
                      border: "none",
                      boxShadow: "none",
                      cursor: "pointer",
                      minHeight: "10px",
                      padding: "0px",
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: "20px",
                      width: "8rem",
                      padding: "0 8px",
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: "0",
                      padding: "0",
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      margin: "0",
                      padding: "0",
                      lineHeight: "20px",
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: "#1E1B59",
                      fontSize: "10px",
                      textTransform: "capitalize",
                      marginTop: "-1px",
                      lineHeight: "20px",
                    }),

                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: "20px",
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: "10px",
                      cursor: "pointer",
                    }),
                  }}
                />
                <div className="w-10 z-50 shrink-0 flex justify-center items-center">
                  {loading ? (
                    <AiOutlineLoading3Quarters className="text-xs animate-spin" />
                  ) : (
                    <IoSearchOutline className="cursor-pointer text-edvion_black text-opacity-50 text-md" />
                  )}
                </div>
              </div>

              <div className="flex justify-end items-center flex-1 w-full max-w-[24rem] gap-2">
                <div className="w-full max-w-[15rem]">
                  <TransactionDateFilter
                    setType={setDateRangeLabel}
                    type={dateRangeLabel}
                    selectedRange={selectedRange}
                    setSelectedRange={setSelectedRange}
                    setIsDateRangeIsSelected={setIsDateRangeIsSelected}
                    refetch={() => {
                      setIsDateRangeIsSelected(true);
                      setCurrentPage((prev) => (prev === 1 ? prev : 1));
                    }}
                  />
                </div>
                {((isRedirectedUser ? tokenData?.role : user?.getMerchantQuery?.role) === Access.OWNER || (isRedirectedUser ? tokenData?.role : user?.getMerchantQuery?.role) === Access.ADMIN) && (
                  <button
                    onClick={() => setShowModal(!showModal)}
                    className="bg-[#1E1B59] text-white text-xs font-medium px-4 py-2 rounded-[4px] whitespace-nowrap"
                  >
                    Create Plan
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center">
              <RowsPerPageSelect
                setItemsPerRow={handleItemsPerPageChange}
                itemsPerRow={itemsPerRow}
                className="justify-start"
              />
              {dateRangeLabel !== "" && isDateRangeIsSelected && (
                <div className="text-sm m-2">
                  <button
                    onClick={() => {
                      setDateRangeLabel("");
                      setIsDateRangeIsSelected(false);
                      setSelectedRange(DEFAULT_RANGE);

                      refetch({
                        filter: {
                          page: 1,
                          limit: itemsPerRow.name,
                          search: searchText?.trim() || undefined,
                          school_id: schoolId || undefined,
                        },
                      });

                      setCurrentPage(1);
                    }}
                    className="bg-[#6687FFCC] font-medium flex items-center rounded-[4px] text-white px-4 py-2"
                  >
                    {dateRangeLabel}
                    <HiMiniXMark className="text-lg ml-1" />
                  </button>
                </div>
              )}
            </div>
          </div>
        }
        data={[
          [
            "Sr.No",
            "Created On",
            "Plan ID",
            "Plan Name",
            "Plan Type",
            "Recurring Amt.",
            "Maximum Amt.",
            "Charge Customer Once Every",
            "Max. No. Of Debits",
            "Description",
          ],
          ...(plansData?.data?.map((p: any, idx: number) => [
            <div> {(currentPage - 1) * itemsPerRow.name + 1 + idx}</div>,
            <div className="truncate max-w-[10rem]">
              {p?.createdAt.split("T")[0] ? p?.createdAt.split("T")[0] : "N/A"}
            </div>,

            <div className="truncate max-w-[10rem]">
              {p?.plan_id ? p?.plan_id : "N/A"}
            </div>,

            <div className="truncate max-w-[10rem]">
              {p?.plan_name ? p?.plan_name : "N/A"}
            </div>,

            <div className="truncate max-w-[10rem]">
              {p?.plan_type ? p?.plan_type : "N/A"}
            </div>,

            <div className="truncate max-w-[10rem]">
              {amountFormat(
                Number.isFinite(p?.plan_recurring_amount) &&
                  p.plan_recurring_amount > 0
                  ? p.plan_recurring_amount
                  : 0
              )}
            </div>,

            <div className="truncate max-w-[10rem]">
              {amountFormat(
                Number.isFinite(p?.plan_max_amount) && p.plan_max_amount > 0
                  ? p.plan_max_amount
                  : 0
              )}
            </div>,

            <div className="truncate max-w-[10rem]">
              {p?.plan_intervals ? p?.plan_intervals : "N/A"}
            </div>,

            <div className="truncate max-w-[10rem]">
              {p?.plan_max_cycles ? p?.plan_max_cycles : "N/A"}
            </div>,

            <div className="truncate max-w-[10rem]">
              {p?.plan_note ? p?.plan_note : "N/A"}
            </div>,
          ]) || []),
        ]}
        footer={
          <div className="flex justify-center mt-2 w-full">
            <Pagination
              onPageChange={handlePageChange}
              currentPage={currentPage}
              totalPages={Math.ceil(plansData?.pagination?.totalPages)}
            />
          </div>
        }
      />
      <Modal
        className="max-w-lg w-full"
        open={showModal}
        setOpen={(v:boolean) => {
          setShowModal(v);
          if (!v) {
            refetch();
          }
        }}
        title="Create Plan"
      >
        <CreatePlanForm setOpen={setShowModal} onCreated={() => refetch()} />
      </Modal>
    </div>
  );
}

export default Plans;
